import { Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';

import SsmTunnel from '../../../shared/SsmTunnel';
import { installSsmWebSocket, SENTINEL_URL } from '../../../shared/SsmWebSocket';

// Lazy-load the iron-remote-desktop web component + its (multi-MB) wasm so it
// lands in its own chunk, only fetched when an RDP session is actually opened.
// Memoized: the wasm is initialized once per page.
let rdpModulePromise;
function loadRdp() {
  if (!rdpModulePromise) {
    rdpModulePromise = (async () => {
      const [rdp] = await Promise.all([
        import('@devolutions/iron-remote-desktop-rdp'),
        import('@devolutions/iron-remote-desktop'), // registers <iron-remote-desktop>
      ]);
      await rdp.init('INFO');
      return rdp;
    })();
  }
  return rdpModulePromise;
}

// Renders an in-browser RDP session over the SSM tunnel. ironrdp-web speaks
// RDCleanPath to the box-side gateway; its TLS is terminated there. We feed it
// our SsmTunnel by swapping the WebSocket it opens for SENTINEL_URL.
const RdpDialog = ({ open, session, credentials, onClose, onConnected }) => {
  const containerRef = React.useRef(null);
  // Hold the latest onClose/onConnected in refs so the connect effect below does
  // NOT depend on them. The parent passes inline callbacks that change identity
  // every render; if they were dependencies, any parent re-render would re-run
  // the effect — tearing down the tunnel and calling session.destroy(), which
  // deletes the live SSM session mid-RDP.
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const onConnectedRef = React.useRef(onConnected);
  onConnectedRef.current = onConnected;

  React.useEffect(() => {
    if (!open || !session || !credentials) return undefined;

    let cancelled = false;
    let teardown = () => {};

    loadRdp()
      .then(({ Backend }) => {
        if (cancelled || !containerRef.current) return;

        const tunnel = new SsmTunnel(session, { portForward: true });
        const restoreWebSocket = installSsmWebSocket(tunnel);

        const el = document.createElement('iron-remote-desktop');
        el.setAttribute('scale', 'fit');
        el.module = Backend;

        el.addEventListener('ready', async event => {
          if (cancelled) return;
          const ui = event.detail?.irgUserInteraction ?? event.detail;
          try {
            // Clipboard (CLIPRDR): auto-sync the browser clipboard both ways.
            // The browser prompts for clipboard-read permission on first paste.
            ui.setEnableClipboard(true);
            ui.setEnableAutoClipboard(true);
            const config = ui
              .configBuilder()
              .withUsername(credentials.username)
              .withPassword(credentials.password)
              // Destination + auth token are required by the builder but
              // ignored by our gateway (it pins its own target; SSM is the
              // access boundary). proxyAddress routes through SsmWebSocket.
              .withDestination('rdp')
              .withProxyAddress(SENTINEL_URL)
              .withAuthToken('ssm')
              .build();
            const sessionInfo = await ui.connect(config);
            ui.setVisibility(true);
            // Desktop is up — let the parent dismiss its "Connecting" dialog.
            if (!cancelled) onConnectedRef.current?.();
            await sessionInfo.run();
          } catch (err) {
            // ironrdp-web throws an IronError (a wasm object that logs as
            // `A {__wbg_ptr}`); pull out the real reason instead of the wrapper.
            const kind = typeof err?.kind === 'function' ? err.kind() : undefined;
            const backtrace =
              typeof err?.backtrace === 'function' ? err.backtrace() : undefined;
            // eslint-disable-next-line no-console
            console.error('RDP session error', { kind, backtrace, err });
          } finally {
            if (!cancelled) onCloseRef.current();
          }
        });

        containerRef.current.appendChild(el);

        teardown = () => {
          try {
            el.remove();
          } catch (err) {
            // ignore
          }
          restoreWebSocket();
          tunnel.close();
        };
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to load the RDP client', err);
        if (!cancelled) onCloseRef.current();
      });

    return () => {
      cancelled = true;
      teardown();
      if (session) session.destroy?.();
    };
  }, [open, session, credentials]);

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <IconButton
        onClick={onClose}
        size="small"
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: '#fff' }}
      >
        <CloseIcon />
      </IconButton>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </Dialog>
  );
};

export default RdpDialog;
